import { Link } from "react-router-dom";
import { SEO } from "@/components/seo";
import { FiMail, FiPhone, FiMapPin, FiExternalLink, FiCheck } from "@/lib/icons";

const afcaDetails = {
  email: "info@afca.org.au",
  phone: "1800 931 678",
  website: "www.afca.org.au",
};

const afgComplaints = {
  email: "complaints@afgonline.com.au",
  phone: "+61 08 9420 7888",
  address: "4/100 Havelock Street, West Perth, 6005",
};

export function ComplaintsPage() {
  return (
    <>
      <SEO
        title="Complaints"
        description="How to lodge a complaint and our dispute resolution process. We are committed to resolving complaints genuinely, efficiently and effectively."
        url="/complaints"
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <p className="section-label mb-4">Support</p>
          <h1 className="font-serif text-4xl md:text-5xl mb-4">
            Streamlined Complaint Resolution
          </h1>
          <p className="text-lg text-muted-foreground">
            We are committed to resolving complaints genuinely, efficiently and effectively.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="prose prose-lg max-w-none">
            {/* How to lodge */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">How to lodge a complaint</h2>
              <p className="text-muted-foreground mb-6">
                If you would like to make a complaint you can do so through our internal
                dispute resolution process which is designed to deal with complaints
                genuinely, efficiently and effectively.
              </p>
              <p className="text-muted-foreground mb-6">
                You can lodge a complaint by contacting our Complaints Officer in any of
                the following ways:
              </p>

              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <a
                  href={`mailto:${afgComplaints.email}`}
                  className="flex flex-col items-center p-6 bg-white rounded-2xl border border-border/50 hover:border-primary/30 hover:shadow-md transition-all group"
                >
                  <div className="p-3 bg-accent/20 rounded-xl mb-3 group-hover:bg-accent/30 transition-colors">
                    <FiMail className="size-6 text-primary" />
                  </div>
                  <span className="text-xs text-muted-foreground mb-1">Email</span>
                  <span className="text-sm font-medium text-primary text-center break-all">
                    {afgComplaints.email}
                  </span>
                </a>

                <a
                  href={`tel:${afgComplaints.phone.replace(/\s/g, "")}`}
                  className="flex flex-col items-center p-6 bg-white rounded-2xl border border-border/50 hover:border-primary/30 hover:shadow-md transition-all group"
                >
                  <div className="p-3 bg-accent/20 rounded-xl mb-3 group-hover:bg-accent/30 transition-colors">
                    <FiPhone className="size-6 text-primary" />
                  </div>
                  <span className="text-xs text-muted-foreground mb-1">Phone</span>
                  <span className="text-sm font-medium text-primary">
                    {afgComplaints.phone}
                  </span>
                </a>

                <div className="flex flex-col items-center p-6 bg-white rounded-2xl border border-border/50">
                  <div className="p-3 bg-accent/20 rounded-xl mb-3">
                    <FiMapPin className="size-6 text-primary" />
                  </div>
                  <span className="text-xs text-muted-foreground mb-1">Mail</span>
                  <span className="text-sm font-medium text-center">
                    {afgComplaints.address}
                  </span>
                </div>
              </div>

              <div className="bg-muted/30 rounded-xl p-4">
                <p className="text-sm text-muted-foreground">
                  Please explain the details of your complaint as clearly as you can.
                  If you need assistance explaining your complaint, please let the
                  Complaints Officer know, so they can make the necessary arrangements.
                </p>
              </div>
            </div>

            {/* How will your complaint be dealt with */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">
                How will your complaint be dealt with?
              </h2>
              <p className="text-muted-foreground mb-6">
                When we receive a complaint, we will do our best to resolve it promptly.
                To help us do this we ask that you provide:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <FiCheck className="size-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">
                    All essential and relevant information, documents, written statements
                    and any other materials that may assist in resolving the complaint
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <FiCheck className="size-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">
                    Any additional information that we may reasonably request
                  </span>
                </li>
              </ul>
            </div>

            {/* What if not satisfied */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">
                What if you are not satisfied?
              </h2>
              <p className="text-muted-foreground mb-6">
                If you are not satisfied with our response to your complaint or we do not
                reach agreement within the prescribed timeframe, you may refer the
                complaint to our external dispute resolution scheme, the Australian
                Financial Complaints Authority (AFCA).
              </p>

              <div className="bg-primary text-primary-foreground rounded-2xl p-6 md:p-8">
                <h3 className="font-semibold text-lg mb-4">
                  Australian Financial Complaints Authority
                </h3>
                <p className="text-primary-foreground/80 text-sm mb-6">
                  This is a free service which provides you with an independent mechanism
                  to resolve certain complaints.
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  <a
                    href={`mailto:${afcaDetails.email}`}
                    className="flex items-center gap-3 p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-colors"
                  >
                    <FiMail className="size-5 text-accent" />
                    <div>
                      <span className="text-xs text-primary-foreground/85 block">Email</span>
                      <span className="text-sm font-medium">{afcaDetails.email}</span>
                    </div>
                  </a>

                  <a
                    href={`tel:${afcaDetails.phone.replace(/\s/g, "")}`}
                    className="flex items-center gap-3 p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-colors"
                  >
                    <FiPhone className="size-5 text-accent" />
                    <div>
                      <span className="text-xs text-primary-foreground/85 block">Phone</span>
                      <span className="text-sm font-medium">{afcaDetails.phone}</span>
                    </div>
                  </a>

                  <a
                    href={`https://${afcaDetails.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-colors md:col-span-2"
                  >
                    <FiExternalLink className="size-5 text-accent" />
                    <div>
                      <span className="text-xs text-primary-foreground/85 block">Online</span>
                      <span className="text-sm font-medium">{afcaDetails.website}</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-cream rounded-2xl p-8">
              <h2 className="font-serif text-xl mb-4">Related Information</h2>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/privacy-policy"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms-conditions"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
                >
                  Terms & Conditions
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
